import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext, apiClient } from '../App';
import { registrationCategories } from './PushRegistrationEvidence';

const shown = value => value === true ? 'Yes' : value === false ? 'No' : 'Not established';
const time = value => value || 'Unavailable';

export function PushDiagnosticsView({ data }) {
  const report = data.registrations;
  const operation = data.latest_operation;
  return <div className="space-y-3 text-sm min-w-0 [overflow-wrap:anywhere]" data-testid="push-diagnostics-evidence">
    <p>Evidence checked: {time(data.checked_at)}. Eligibility is evaluated at read time.</p>
    <div>{Object.entries(registrationCategories).map(([key, label]) => <p key={key}>{label}: {report.counts[key] || 0}</p>)}</div>
    {!report.registrations.length && <p>No registration records.</p>}
    {!!report.registrations.length && <details><summary>Token-free registration evidence ({report.registrations.length})</summary>
      {report.registrations.map((row, index) => <div className="border-t py-2" key={row.reference || index}>
        <p>Reference: {row.reference || 'Unavailable'} · {registrationCategories[row.category]}</p>
        <p>Platform: {row.platform}; environment: {row.environment}; status: {row.active_status}</p>
        <p>Generation evidence: {shown(row.generation_present)}; device-binding evidence: {shown(row.device_evidence_present)}</p>
        <p>Authoritative ownership/generation match: {shown(row.authoritative_bindings_match)}</p>
        <p>Exclusion reasons: {row.reasons.length ? row.reasons.map(x => x.replace(/_/g, ' ')).join(', ') : 'None observed'}</p>
        <p>Created: {time(row.created_at)}; updated: {time(row.updated_at)}</p>
      </div>)}
    </details>}
    <p>Version/build: {data.observations.version_build}. Device-reported permission: {data.observations.permission}.</p>
    <section><h4 className="font-semibold">Latest retained registration operation</h4>
      {operation ? <><p>Reference: {operation.reference}; outcome: {operation.state}; action: {operation.action || 'Not retained'}; recorded: {time(operation.created_at)}</p>
        {operation.failure_reason && <p>{operation.failure_reason}</p>}</> : <p>No retained operation evidence. This does not establish whether native registration was attempted.</p>}
    </section>
    <section><h4 className="font-semibold">Recent push traces</h4>
      <p>Latest {data.trace_limit} retained push delivery records. No device-presentation confirmation is collected here.</p>
      {!data.recent_push_traces.length && <p>No retained push trace evidence; no recorded failure is established.</p>}
      {data.recent_push_traces.map(row => <details key={row.reference} className="border-t py-2"><summary>{time(row.created_at)} · {row.status} · {row.policy_skip ? 'Recorded policy skip' : 'Push trace'}</summary>
        <p>Reference: {row.reference}; reason: {row.reason || 'Not retained'}; first failed stage: {row.failure_stage || 'Not established'}</p>
        <p>Environment: {row.environment || 'Not retained'}; provider: {row.provider || 'Not retained'}</p>
        <p>Provider attempted: {shown(row.provider_attempted)}; APNs attempted: {shown(row.apns_attempted)}</p>
        <p>APNs result: {row.apns_result || 'No retained APNs result'}</p>
      </details>)}
    </section>
    <section><h4 className="font-semibold">Recorded activity evidence</h4>
      {data.activity.latest_recorded_event_at ? <p>Latest stored engagement event: {data.activity.latest_recorded_event_at}</p> : <p>No retained engagement-event timestamp. Signup-date fallback: {time(data.activity.account_created_at)}.</p>}
      <p>{data.activity.definition} Missing activity is not proof the app was unused.</p>
    </section>
    <p><strong>Next step:</strong> {data.next_action}</p>
    <p>Delivery: Production describes safe routing. Device permission, server registration eligibility, APNs acceptance and an iPhone displaying a notification are separate facts.</p>
  </div>;
}

export default function AdminPushDiagnostics({ userId, targetUsername }) {
  const { user, token } = useContext(AuthContext);
  const [expanded, setExpanded] = useState(false);
  const [result, setResult] = useState(null);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const contextRef = useRef(null);
  const identity = `${user?.id || ''}:${user?.role || ''}:${userId || ''}:${targetUsername || ''}`;
  // Object identity creates a fresh generation even for A -> B -> A. Rendering hides old results immediately.
  if (contextRef.current?.identity !== identity || contextRef.current?.token !== token) contextRef.current = { identity, token };
  const context = contextRef.current;
  const allowed = user?.role === 'admin' && !!userId;
  const data = allowed && result?.context === context ? result.value : null;
  useEffect(() => {
    setResult(null); setError(null); setPending(null);
    if (!allowed || !expanded) return;
    const controller = new AbortController();
    let active = true;
    const current = () => active && contextRef.current === context;
    setPending(context);
    const timeout = setTimeout(() => {
      if (!current()) return;
      active = false; controller.abort(); setPending(null);
      setError({ context, message: 'Diagnostics read timed out. Retry this read; no registration or send was requested.' });
    }, 15000);
    apiClient.get(`/admin/users/${userId}/push-diagnostics`, { signal: controller.signal }).then(response => {
      if (current()) setResult({ context, value: response.data });
    }).catch(() => {
      if (current()) setError({ context, message: 'Diagnostics unavailable. Retry the read; unavailable evidence is not a recorded failure.' });
    }).finally(() => { clearTimeout(timeout); if (current()) setPending(null); });
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, [context, allowed, expanded, refresh, userId]);
  if (!allowed) return null;
  return <section className="mt-4 rounded border p-3 min-w-0 [overflow-wrap:anywhere]" aria-label="Push Diagnostics">
    <button type="button" aria-expanded={expanded} onClick={() => setExpanded(x => !x)} className="font-semibold underline">Push Diagnostics — {expanded ? 'Collapse' : 'Expand'}</button>
    {expanded && <div className="mt-3">
      {pending === context && <p>Reading push evidence…</p>}
      {error?.context === context && <p role="alert">{error.message}</p>}
      {data && <PushDiagnosticsView data={data} />}
      <button type="button" disabled={pending === context} onClick={() => setRefresh(x => x + 1)} className="mt-2 underline">Refresh diagnostics</button>
    </div>}
  </section>;
}
